// src/pages/.../UpdateArticle.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

import ArticleCard from "../../../shared/components/ArticleCard";
import "../../../shared/styles/CardSlideForm.css";
import "../../../shared/styles/UtillitiesComponent.css";


// 🔥 Firestore
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../shared/libs/firebaseConfig";

import { Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";

import {
  BsChatSquareHeart,
  BsChevronLeft,
  BsChevronRight,
} from "react-icons/bs";

// ------- helper -------
function formatThaiDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    // year: "numeric",
  });
}

function getFirstImageSrc(contentBlocks = []) {
  const imgBlock = contentBlocks.find((b) => b.type === "image" && b.src);
  return imgBlock?.src || "https://placehold.co/1000x650/png";
}

export default function UpdateArticle() {
  // 💡 เก็บบทความทั้งหมดสำหรับสร้าง badge
  const [allArticlesBase, setAllArticlesBase] = useState([]);
  // 💡 บทความที่แสดงตาม badge ปัจจุบัน
  const [articles, setArticles] = useState([]);

  const [selectedSub, setSelectedSub] = useState("all"); // "all" = ทั้งหมด
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const slideRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const initialScrollRef = useRef(null);

  // 🧩 helper ใช้ map doc -> object เดียวกันทั้ง initial / filter
  const mapDocToArticle = (doc) => {
    const data = doc.data();
    const createdAt = data.createdAt?.toDate
      ? data.createdAt.toDate()
      : null;

    return {
      id: doc.id,
      slug: data.slug,
      image: data.cover || getFirstImageSrc(data.contentBlocks),
      title: data.title || "(ไม่มีชื่อบทความ)",
      author: data.author?.displayName || "ไม่ระบุผู้เขียน",
      date: formatThaiDate(createdAt),
      level: data.level || data.levelDisplay || "general",
      views: data.views ?? 0,
      readTime: data.readTime ?? 0,

      subCategoryName: data.subCategory?.name || "ทั่วไป",
      subCategorySlug: data.subCategory?.slug || "",

      categoryName: data.category?.name || "ทั่วไป",
      categorySlug: data.category?.slug || "",

      tagSlugs: data.tagSlugs || [],
    };
  };

  // 🔹 โหลด "ทั้งหมด" รอบแรก
  useEffect(() => {
    const fetchInitialArticles = async () => {
      try {
        setLoading(true);
        setError("");

        const qAll = query(
          collection(db, "articles"),
          where("publicStatus", "==", true),
          where("status", "==", "published"),
          orderBy("createdAt", "desc"),
          limit(50)
        );

        const snap = await getDocs(qAll);
        const list = snap.docs.map(mapDocToArticle);

        setAllArticlesBase(list); // สำหรับสร้าง badge
        setArticles(list);        // แสดงผลค่า default
        setSelectedSub("all");
      } catch (err) {
        console.error(err);
        setError("ไม่สามารถโหลดบทความได้");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialArticles();
  }, []);

  // 🔹 ฟังก์ชัน query ตาม subCategory จาก Firestore
  const fetchArticlesBySubcategory = async (subSlug) => {
    try {
      setLoading(true);
      setError("");

      const baseConstraints = [
        where("publicStatus", "==", true),
        where("status", "==", "published"),
        orderBy("createdAt", "desc"),
        limit(50),
      ];

      let qSub;

      if (subSlug === "all") {
        // ทั้งหมด – จริง ๆ ใช้ allArticlesBase ก็ได้
        qSub = query(collection(db, "articles"), ...baseConstraints);
      } else {
        // หมวดหมู่ย่อย – ใช้ field subCategory.slug (ตาม structure ใน Firestore)
        qSub = query(
          collection(db, "articles"),
          where("publicStatus", "==", true),
          where("status", "==", "published"),
          where("subCategory.slug", "==", subSlug),
          orderBy("createdAt", "desc"),
          limit(50)
        );
      }

      const snap = await getDocs(qSub);
      const list = snap.docs.map(mapDocToArticle);
      setArticles(list);
    } catch (err) {
      console.error(err);
      setError("ไม่สามารถโหลดบทความตามหมวดหมู่ได้");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 กดที่ badge
  const handleBadgeClick = (subSlug) => {
    setSelectedSub(subSlug);

    // ถ้าอยากใช้ข้อมูลที่โหลดรอบแรกมา filter ในฝั่ง client แทน query ใหม่:
    // if (subSlug === "all") {
    //   setArticles(allArticlesBase);
    // } else {
    //   setArticles(
    //     allArticlesBase.filter(
    //       (a) => a.subCategorySlug === subSlug
    //     )
    //   );
    //   }
    // return;

    // 👉 ตาม requirement: query จาก Firestore ใหม่
    fetchArticlesBySubcategory(subSlug);
  };

  // 🔹 badge subcategory (สร้างจาก allArticlesBase)
  const subcategoryBadges = useMemo(() => {
    const map = new Map();
    allArticlesBase.forEach((a) => {
      const slug = a.subCategorySlug || a.subCategoryName;
      const name = a.subCategoryName;
      if (!slug || !name) return;
      if (!map.has(slug)) {
        map.set(slug, name);
      }
    });
    return Array.from(map, ([slug, name]) => ({ slug, name }));
  }, [allArticlesBase]);

  // 🔹 แยก row บทความ
  const row1 = articles.slice(0, 2);
  const row2 = articles.slice(2);

  // 🔹 จัดการลูกศร slide
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;

    el.scrollLeft = 0;

    const updateArrows = () => {
      const elNow = slideRef.current;
      if (!elNow) return;

      const { scrollLeft, scrollWidth, clientWidth } = elNow;

      if (initialScrollRef.current === null) {
        initialScrollRef.current = scrollLeft;
      }

      if (scrollWidth <= clientWidth + 1) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const base = initialScrollRef.current ?? 0;

      const atStart = scrollLeft <= base + 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    };

    updateArrows();

    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);

    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [articles]);

  const handleSlideNext = () => {
    const el = slideRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({ left: scrollAmount, behavior: "smooth" });
    setHasInteracted(true);
  };

  const handleSlidePrev = () => {
    const el = slideRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    setHasInteracted(true);
  };

  return (
    <>
      <h3>
        <strong>อัพเดทล่าสุด</strong>
      </h3>

      {loading && (
        <div
          className="text-center"
          style={{ marginTop: "100px", marginBottom: "1000px" }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}

      {!loading && (
        <>
          {/* 🔹 badge หมวดหมู่ (ทั้งหมด + subCategory จาก Firestore) */}
          <div className="subcategory-scroll mb-0">
            {/* badge: ทั้งหมด */}
            <Button
              key="all"
              className={`border-1 btn-sm subcategory-btn ${
                selectedSub === "all" ? "active" : ""
              }`}
              variant="outline-dark"
              onClick={() => handleBadgeClick("all")}
            >
              ทั้งหมด
            </Button>

            {/* badge: แต่ละ subCategory */}
            {subcategoryBadges.map((cat) => (
              <Button
                key={cat.slug}
                className={`border-1 btn-sm subcategory-btn ${
                  selectedSub === cat.slug ? "active" : ""
                }`}
                variant="outline-dark"
                onClick={() => handleBadgeClick(cat.slug)}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          <br />
        </>
      )}


      

      {error && <div className="text-danger my-3">{error}</div>}

      {!loading && !error && (
        <>
          {/* แถวแรก 2 การ์ดใหญ่ */}
          <div className="row row-1 g-4 mb-4">
            {row1.map((a) => (
              <div key={a.id} className="col-lg-6 col-md-6 col-12">
                <Link
                  to={`/articles/${a.slug || a.id}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <ArticleCard article={a} />
                </Link>
              </div>
            ))}
          </div>

          {/* แถวถัดไปเป็นการ์ด slide + ลูกศรซ้ายขวา */}
          <div className="slide-wrapper">
            {hasInteracted && canScrollLeft && (
              <button
                type="button"
                className="slide-arrow slide-arrow-left"
                onClick={handleSlidePrev}
              >
                <BsChevronLeft size={22} />
              </button>
            )}

            <div
              className="article-slide-container"
              ref={slideRef}
              onTouchStart={() => setHasInteracted(true)}
              onMouseDown={() => setHasInteracted(true)}
            >
              {row2.map((a) => (
                <div key={a.id} className="slide-item">
                  <Link
                    to={`/articles/${a.slug || a.id}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <ArticleCard article={a} />
                  </Link>
                </div>
              ))}
            </div>

            {canScrollRight && (
              <button
                type="button"
                className="slide-arrow slide-arrow-right"
                onClick={handleSlideNext}
              >
                <BsChevronRight size={22} />
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
